import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";

import { ASSETS } from "../../../../utils/config";
import { prefersReducedMotion } from "../../../../hooks/useReducedMotion";
import { useBreakpoint } from "../../../../utils/breakpoints";
// QA-1265 — the ONE shared data-driven Wheel renderer (WheelComponent -> D3
// createPie; G-19: never a hand-rolled SVG wheel), the same one ExploreWheelCard
// / Gallery WheelCard use, so a collection's cover adopts its SHOWCASED wheel's
// real center portrait + segment fills + frame instead of a flat cropped photo.
import WheelComponent from "../../../WheelComponent";
// gordon-0920-default-wheel-and-create-wheel-hero-geometry-must-match-
// standard-wheel-REPEAT-COMPLAINT — the DEFAULT (no-segments) cover used to be
// <BrandWheel>, a second, differently-parameterized geometry (own divider gap,
// own ratio, own edge-fill CSS hack) that never matched the D3 wheel-art path
// above pixel-for-pixel. DefaultWheelRing renders the SAME WheelComponent
// engine with a fixed placeholder segment list, so both branches share one
// geometry.
import DefaultWheelRing from "../../../D3Wheels/DefaultWheelRing";
import { resolveWheelCardPortraitUrl } from "../../../../utils/wheelCardPortrait";
import { getWheelVisibilityOption } from "../../../../constants/wheelVisibility";
import { withPaletteResolvedColors } from "../../../SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteFromFill";
import { SEGMENT_PALETTE_COLOR_ENABLED } from "../../../SegmentBuilder/Shared/SegmentStyling/logic/segmentPaletteColorFlag";
import {
  FolderTileV2,
  CollectionCoverRing,
  CollectionCoverFrame,
  CollectionCoverImage,
  CollectionCoverWheelArt,
  CollectionCoverActions,
  CollectionVisibilityIcon,
  CollectionLabelRow,
  CollectionLabel,
  COLLECTION_SPRING_LOAD_DELAY_MS,
  COLLECTION_TREMBLE_MS,
} from "./styles";
import { collectionCardLabel } from "./collectionsCopy";

// #920 §2 / #920d — the Instagram-style CIRCULAR collection card.
//
// REPLACES the office-folder glyph. The card's image is a THUMBNAIL OF ONE
// WHEEL inside the collection (resolved by collectionCover.js and handed in as
// `cover`); a collection with nothing in it yet renders the app's own DEFAULT
// iYuga wheel/logo asset (ASSETS.wheelLogoFallback) — #920d retires the old
// letter-monogram fallback, and neither ever falls back to the folder icon.
//
// Purely presentational + forwardRef, so the drag/drop wiring (FolderTileDnd)
// can attach its ref without this file knowing anything about react-dnd. The
// kebab is passed in as `actions` for the same reason: the card renders the
// owner's EXISTING menu, it does not build or filter one, which is how the
// "customization / settings / permissions / kebab unchanged" constraint is kept
// structurally rather than by inspection.
//
// #920d §5 — the actions overlay is HIDDEN by default and only MOUNTED (not
// just visually hidden) while the card is hovered/focused, or while its menu
// is already open (`forceShowActions`, set by FolderTileDnd from the owner's
// `selectedMenuId` so an open dropdown never gets yanked out of the DOM by the
// pointer leaving the circle mid-click). Tracked as component state rather
// than a CSS-only `:hover` rule so "hidden" is something a DOM query can prove
// — jsdom has no real `:hover` pseudo-class support to assert against.
//
// TITLE TRUNCATION is CSS (`text-overflow: ellipsis` on CollectionLabel) rather
// than the old JS `substring(0, 26) + "…"`: at card width the break point
// depends on the actual glyphs, and a fixed character count either clips early
// on narrow strings or overflows on wide ones. The full name is always
// available via the tooltip and the aria-label.

const CollectionCard = React.forwardRef(
  (
    {
      collection,
      cover = null,
      isPrivate = false,
      actions = null,
      forceShowActions = false,
      isOwner = false,
      onOpen = () => {},
      isOver = false,
      canDrop = false,
      isDragging = false,
      isInvalid = false,
      isWheelOver = false,
      selected = undefined,
      onReorderKeyDown = null,
      onSpringOpen = null,
      ...rest
    },
    ref
  ) => {
    const title = collection?.title || collection?.name || "";
    const open = () => onOpen(collection);
    // "Latest ref" so the spring-load timer effect below can call the
    // current handler without listing it (and thus `collection`/`onOpen`/
    // `onSpringOpen`, which change identity every render) in its dependency
    // array — doing so would tear down and restart the ~1s timer on every
    // re-render instead of once per drag-over.
    //
    // gordon-0920-gallery-drag-ghost — a drag-hover spring-open must NEVER
    // fire the real `onOpen` navigation: that swaps the current level's
    // rendered wheel grid, unmounting the still-being-dragged card's DOM
    // node mid native-HTML5-drag, which kills the browser's drag session and
    // orphans both the drop (early, into the first collection only) and the
    // drag-preview ghost (nothing ever fires a clean dragend/drop after
    // that). `onSpringOpen`, when the caller wires it, is a non-committing
    // "open a preview in place" signal instead — the caller keeps the real
    // grid mounted and shows the opened collection's tiles as an overlay, so
    // the drag session survives and can continue deeper. A plain click's
    // `onOpen` (below, on the card itself) is unaffected either way.
    const fireOpenRef = useRef(null);
    fireOpenRef.current = onSpringOpen
      ? () => onSpringOpen(collection)
      : open;

    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    // ISP-9999 (gordon-0913-collections-hidden-icon-shows-at-rest-regression)
    // — the ONE hover-reveal rule every badge/action overlay on this card
    // shares: mounted (not just CSS-hidden) only while hovered, focused, or
    // `forceShowActions` (an already-open dropdown). Previously the kebab
    // (`showActions`) and the visibility icon (`showVisibilityLabel`) each
    // independently re-derived this same condition, and the Hide-Contents
    // badge below skipped it entirely (always mounted) — the "stray hide
    // icon at rest" bug (row gordon-collections-stray-hide-icon, present
    // since #920's original 2026-08-20 introduction, PR#4269). Computing it
    // once here means a future badge can't reintroduce the same gap by
    // forgetting to copy the condition.
    const isRevealed = isHovered || isFocused || forceShowActions;
    // stgqa-0927 gate-e (390px phone): a touch screen has no hover, and a tap
    // on the card opens the Collection, so a hover-only kebab never appeared
    // and the owner could not reach Share/Rename/Visibility/Delete on a phone.
    // On a touch device the kebab stays mounted at rest. Only the kebab: the
    // other hover badges keep the at-rest rule above.
    const { isTouch } = useBreakpoint();
    const showActions = Boolean(actions) && (isRevealed || isTouch);

    // ISP-9999 (Gordon 2026-08-28) — owner-only visibility status label,
    // beneath the kebab. Gated on `isOwner` (NOT on `actions`/`showActions`,
    // which also require the kebab to actually have menu items) so the label
    // still appears on hover even in the edge case of an owner card with no
    // kebab menu items, but ties to the SAME hover/focus/forceShow triggers
    // the kebab uses so both surface together. `collection.visibility` is the
    // wire value (1/2/3) shared with Wheel.visibility.
    const visibilityOption = isOwner
      ? getWheelVisibilityOption(collection?.visibility)
      : null;
    const showVisibilityLabel = Boolean(visibilityOption) && isRevealed;

    // #951 — spring-load: a Wheel dragged over this collection for
    // COLLECTION_SPRING_LOAD_DELAY_MS trembles the tile, then opens it — via
    // `onSpringOpen` (a non-committing in-place preview) when the caller
    // wires one, falling back to the same `onOpen` a click uses otherwise —
    // so a nested subfolder becomes a drop target. `isWheelOver` is
    // FolderTileDnd's `useDrop` collect result —
    // true only while a "wheel" kind drag item is over THIS tile and the drop
    // is actually allowed — so an invalid or folder-onto-folder hover never
    // arms the timer.
    //
    // Reduced-motion: the OS setting is read at the moment the timer fires
    // (not once at mount), so a mid-drag preference change is still honored.
    // The tremble is skipped entirely and the tile opens directly at the same
    // ~1s mark — "no tremble" never means "no open".
    const [isTrembling, setIsTrembling] = useState(false);
    const trembleTimeoutRef = useRef(null);

    useEffect(() => {
      if (!isWheelOver) return undefined;

      const springLoadTimer = setTimeout(() => {
        if (prefersReducedMotion()) {
          fireOpenRef.current();
          return;
        }
        setIsTrembling(true);
        trembleTimeoutRef.current = setTimeout(() => {
          setIsTrembling(false);
          fireOpenRef.current();
        }, COLLECTION_TREMBLE_MS);
      }, COLLECTION_SPRING_LOAD_DELAY_MS);

      return () => {
        clearTimeout(springLoadTimer);
        clearTimeout(trembleTimeoutRef.current);
        setIsTrembling(false);
      };
    }, [isWheelOver]);

    // #920e §3 — an EMPTY collection (no wheel to draw a cover from, so
    // `cover` is null: collectionCover.js precedence 3) shows the DEFAULT iYuga
    // logo wrapped in the brand-wheel ring, exactly like every other default
    // wheel avatar. A collection whose representative wheel has no resolvable
    // image ALSO gets the ring: collectionCover.js resolves that case to the
    // same shared `wheelLogoFallback` asset (never null, so the collection
    // doesn't misreport as empty) rather than a real thumbnail, so visually
    // it's the identical "no picture" case and must render identically.
    //
    // #952 rework — this can't key off `cover == null` alone. At the gallery
    // root a collection's representative wheel is found by matching the
    // already-loaded wheel list against folder membership (collectionCover.js
    // precedence 2); a wheel this hasn't matched yet resolves to `cover ===
    // null` (the ring, above). Opening the collection (breadcrumb enter) loads
    // its OWN children into `collection.items`, which collectionCover.js then
    // prefers (precedence 2 over 3) — so the SAME imageless wheel, now found
    // via `items`, resolves to a non-null cover carrying the fallback logo
    // URL instead. Keying `isEmpty` on the URL rather than on cover-nullness
    // makes the two precedence paths render identically for the "no picture"
    // case, so leaving the collection (breadcrumb Home) can't flip the ring
    // off just because the card's cover was discovered through a different
    // precedence branch than on fresh load.
    const isEmpty = cover == null || cover.url === ASSETS.wheelLogoFallback;

    // QA-1265 (Gordon 2026-08-25) — render the collection's SHOWCASED wheel with
    // the shared D3 WheelComponent so the cover adopts THAT wheel's real center
    // portrait + segment fills + frame, exactly like every other wheel card.
    // collectionCover.js resolves the representative wheel object onto
    // `cover.wheel`; a wheel with segments takes the D3-art path, everything
    // else (an empty collection, or an explicit cover with no wheel object)
    // keeps the flat ring+image fallback below. Segments are sorted by position
    // and palette-resolved the SAME way ExploreWheelCard / Gallery WheelCard do.
    const coverWheel = cover?.wheel || null;
    const coverSegments = useMemo(() => {
      const segs = Array.isArray(coverWheel?.wheel_segments)
        ? coverWheel.wheel_segments
        : [];
      const sorted = [...segs].sort((a, b) => a.position - b.position);
      return withPaletteResolvedColors(sorted, {
        enabled: SEGMENT_PALETTE_COLOR_ENABLED,
      });
    }, [coverWheel]);
    const hasWheelArt = coverSegments.length > 0;

    const wheelArtProps = useMemo(
      () => ({
        userData: {
          id: cover?.wheelId ?? coverWheel?.id ?? "collection-cover",
          // Center portrait = the wheel's still image; createPie falls back to
          // the iYuga logo internally when this is the shared fallback URL, so
          // the center is never bare.
          url: resolveWheelCardPortraitUrl(coverWheel, ASSETS.wheelLogoFallback),
          name: title,
        },
        maxSelectedArcs: 0,
        // Full outer radius (was -10) so segments reach the SVG rim like
        // BrandWheel — the -10 shrink left a ~4.5px white halo on 91px covers.
        arcThickness: 0,
        chartData: coverSegments,
        // Gordon 2026-08-27 (collcover, BUG B) — the wheel art was drawn
        // margin/2 px smaller than the cover frame on every side (createPie.js
        // subtracts `margin` from the container's clientWidth/clientHeight
        // before sizing the circle), reading as a visible ring of empty space
        // inside the round cover. Gordon wants the showcased wheel FULL-BLEED,
        // filling the circle exactly like a normal wheel thumbnail — margin 0.
        margin: 0,
        setChartData: () => {},
        gifHoverPreview: true,
        // #920hover (collections-hover-grow-REDRIVE-live-frames) — Gordon: no
        // grow/motion effect anywhere in a Collection's hover (keep only the
        // kebab + visibility reveals). `gifHoverPreview: true` above is still
        // needed for this cover's OTHER "browse tile" treatment (full-bleed
        // segments, non-interactive, no backing disc) but it also silently
        // opts into WheelComponent's wedge-hover "breath" whenever
        // REACT_APP_SEGMENT_HOVER_MOTION is on (index.jsx:184) — every other
        // small wheel-thumbnail caller (PickerWheelThumb, WheelInfoCard,
        // LinkPreview, MoveToFoldersModal/WheelListItem) never sets
        // gifHoverPreview at all, so they were never exposed to this. Force
        // the wedge motion off explicitly rather than dropping
        // gifHoverPreview entirely, which would also revert the full-bleed/
        // non-interactive cover treatment.
        enableSegmentHoverMotion: false,
        width: "100%",
        height: "100%",
        containerDivStyle: { width: "100%", height: "100%" },
        resize: true,
        // ISP-9999 fe-collections-remove-hover-segment — this cover is a
        // static thumbnail (createPie.js:3383/3432's on-hover "Segment N"
        // tooltip pill is a relic of the old interactive Gallery D3 wheel),
        // same as every other small wheel-thumbnail caller (PickerWheelThumb,
        // WheelInfoCard, LinkPreview, MoveToFoldersModal/WheelListItem).
        config: { showTooltips: false },
      }),
      [coverWheel, coverSegments, title, cover?.wheelId]
    );

    return (
      <FolderTileV2
        ref={ref}
        isOver={isOver}
        canDrop={canDrop}
        isDragging={isDragging}
        isInvalid={isInvalid}
        selected={selected}
        isTrembling={isTrembling}
        // A real (non-hashed) class name, in addition to the styled-prop that
        // drives the actual CSS animation, so the spring-load state is
        // assertable by name rather than by inspecting computed style.
        className={isTrembling ? "tremble" : undefined}
        role="button"
        tabIndex={0}
        data-testid="collection-card"
        aria-label={collectionCardLabel(title, { isPrivate })}
        // Keyboard-reorder hint (Gordon 2026-08-27) — only announced when the
        // caller actually wired reorder-by-keyboard (owner's own gallery),
        // mirroring ArrangeableGrid's "hold Ctrl and press arrow keys" convention
        // for the wheels grid.
        aria-keyshortcuts={
          onReorderKeyDown ? "Control+ArrowLeft Control+ArrowRight" : undefined
        }
        // Only a SELECTABLE card (the visitor's filter cards) is a toggle;
        // the owner's cards open a level and must not claim pressed state.
        aria-pressed={selected === undefined ? undefined : Boolean(selected)}
        // #935: a single click opens the collection (matches every other
        // clickable card in the gallery — nothing else here relies on a
        // single click for drag/select: FolderTileDnd's drag source fires on
        // native pointer-move, not a React onClick, so it isn't affected by
        // this; and GuestView's selectable filter cards pass their own
        // `onClick` in `...rest`, which — spread below, after this one —
        // wins and keeps selecting instead of opening. Enter/Space keep the
        // keyboard path.
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          } else if (onReorderKeyDown) {
            onReorderKeyDown(e);
          }
        }}
        // #920d §5 — hover/focus is what reveals the actions overlay below.
        // `focus` (not `focusin`) is fine here: React normalizes onFocus/
        // onBlur to bubble, so focusing the kebab BUTTON nested inside this
        // tile still reaches this handler and keeps the overlay mounted.
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        {...rest}
      >
        {/* gordon-0923-P1-web-gallery-collections-row-instagram-sizing-no-count
            spec point 1 — the new IG-style ring: a thin 1pt grey circle
            drawn OUTSIDE the cover with a gap, sized to the tile's own full
            width (COLLECTION_RING_SIZE). CollectionCoverFrame keeps its own
            unrelated pre-existing hairline + overflow:hidden clip, now at
            the smaller COLLECTION_COVER_SIZE, centered inside this ring. */}
        <CollectionCoverRing data-testid="collection-cover-ring">
        <CollectionCoverFrame
          data-testid="collection-cover-frame"
          $wheelArt={hasWheelArt}
        >
          {hasWheelArt ? (
            // QA-1265 — the SHOWCASED wheel, rendered by the shared D3
            // WheelComponent (segment arcs + center portrait + frame), fills the
            // round cover. This replaces the flat cropped <img> for any
            // collection whose representative wheel carries segments; the
            // `overflow: hidden` circle of CollectionCoverFrame clips it exactly
            // as it clipped the image. The empty/no-segment fallback below is
            // unchanged.
            <CollectionCoverWheelArt
              data-testid="collection-cover-wheel"
              data-cover-wheel-id={
                cover?.wheelId == null ? undefined : String(cover.wheelId)
              }
              aria-hidden="true"
            >
              <WheelComponent {...wheelArtProps} />
            </CollectionCoverWheelArt>
          ) : (
            <>
              {/* QA-1013 (Gordon 2026-08-24) — the ring is UNIVERSAL for the
                  NON-wheel-art fallback: an empty/default-logo cover carries
                  it so the Collections row stays consistent. (A real
                  showcased wheel now brings its OWN frame via the D3 art
                  above, so it no longer needs this generic ring.) The cover
                  image (logo) layers on TOP, sized to the ring's inner disc;
                  `isEmpty` governs how it sits.
                  gordon-0920-default-wheel-and-create-wheel-hero-geometry-
                  must-match-standard-wheel-REPEAT-COMPLAINT — this ring is
                  now DefaultWheelRing (WheelComponent + a fixed segment
                  list), the SAME engine + full-bleed recipe as the real
                  wheel-art branch above, reusing the SAME CollectionCoverWheelArt
                  wrapper (no CSS scale hack needed: WheelComponent's own
                  margin:0 already fills edge-to-edge). It used to be
                  <BrandWheel>, a second geometry that never matched the D3
                  divider/rim/hairline/ring-thickness pixel-for-pixel. */}
              <CollectionCoverWheelArt
                data-testid="collection-cover-wheel-frame"
                aria-hidden="true"
              >
                <DefaultWheelRing />
              </CollectionCoverWheelArt>

              <CollectionCoverImage
                isEmpty={isEmpty}
                src={cover?.url || ASSETS.wheelLogoFallback}
                alt=""
                aria-hidden="true"
                draggable={false}
                data-testid="collection-cover-image"
                data-cover-source={cover?.source || "default"}
                data-cover-wheel-id={
                  cover?.wheelId == null ? undefined : String(cover.wheelId)
                }
              />
            </>
          )}

        </CollectionCoverFrame>
        </CollectionCoverRing>

        {/* QA-1012 (Gordon 2026-08-24) — the collection lock/private badge was
            REMOVED entirely: a Collection cannot be locked right now, so the
            badge was meaningless and confusing. (Supersedes Kelly #5, which had
            relocated it to the top-left corner.) `isPrivate` is retained for the
            accessible label only. */}

        {/* #920e §5(a) — the kebab badge lives OUTSIDE the overflow:hidden
            cover frame (a direct child of the card), so neither it nor its
            portalled dropdown is clipped by the round cover. Anchored to
            FolderTileV2 (position: relative), overlapping the upper-right
            corner. Still hover/focus-gated via `showActions`. */}
        {showActions && (
          <CollectionCoverActions data-testid="collection-card-actions">
            {actions}
          </CollectionCoverActions>
        )}

        {/* Gordon 2026-09-17 (Option A, gordon-0917-collections-hidden-badge-repeat)
            — the "Contents hidden from the Wheel Grid" eye-slash badge was
            REMOVED from this tile entirely. A tile now shows exactly ONE
            badge on hover: its visibility (Public/Private/Hidden, below).
            The hide-contents state (`gallery_visibility`) is still shown and
            editable in the collection settings modal's existing "Hide
            Contents from Gallery" checkbox — only this tile-level indicator
            is gone. */}

        {showVisibilityLabel && (
          <Tooltip title={visibilityOption.label} arrow>
            <CollectionVisibilityIcon
              data-testid="collection-card-visibility-label"
              visibility={collection?.visibility}
              role="img"
              aria-label={visibilityOption.label}
              tabIndex={0}
            >
              <img
                src={ASSETS.segmentEditor[visibilityOption.icon]}
                width={14}
                height={14}
                alt=""
                aria-hidden="true"
              />
            </CollectionVisibilityIcon>
          </Tooltip>
        )}

        {/* gordon-0923-P1-web-gallery-collections-row-instagram-sizing-no-count
            spec point 2 — no wheel count on this row at all, name only. The
            per-tile `(N)` badge that used to render here moved to a single
            owner-only recursive total on the OPENED collection's header
            instead (OwnerView.jsx, next to the Breadcrumb), never a per-tile
            count in this row. */}
        <CollectionLabelRow>
          <Tooltip title={title} placement="top">
            <CollectionLabel data-testid="collection-card-label">
              {title}
            </CollectionLabel>
          </Tooltip>
        </CollectionLabelRow>
      </FolderTileV2>
    );
  }
);

CollectionCard.displayName = "CollectionCard";

CollectionCard.propTypes = {
  collection: PropTypes.object,
  cover: PropTypes.shape({
    url: PropTypes.string,
    source: PropTypes.string,
    wheelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isPrivate: PropTypes.bool,
  actions: PropTypes.node,
  forceShowActions: PropTypes.bool,
  isOwner: PropTypes.bool,
  onOpen: PropTypes.func,
  isOver: PropTypes.bool,
  canDrop: PropTypes.bool,
  isDragging: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isWheelOver: PropTypes.bool,
  selected: PropTypes.bool,
  onReorderKeyDown: PropTypes.func,
  onSpringOpen: PropTypes.func,
};

export default CollectionCard;
